import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactElement, ReactNode } from "react";

/**
 * Cycle 9 STORY-802: Compare products context.
 *
 * Manages user's "compare cart" — selected products for side-by-side comparison.
 * Limit: max 3 products. Persists to localStorage.
 */

const STORAGE_KEY = "itn_compare_slugs";
const MAX_PRODUCTS = 3;

interface CompareContextValue {
  /** Selected product slugs */
  selectedSlugs: string[];
  /** Add product (no-op if already added or at limit) */
  addProduct: (slug: string) => void;
  /** Remove product */
  removeProduct: (slug: string) => void;
  /** Toggle product (add if not in list, remove if in list) */
  toggleProduct: (slug: string) => boolean;
  /** Check if product is selected */
  isSelected: (slug: string) => boolean;
  /** Clear all selections */
  clear: () => void;
  /** Whether at max capacity */
  isFull: boolean;
  /** Count of selected products */
  count: number;
}

const CompareContext = createContext<CompareContextValue | null>(null);

function loadFromStorage(): string[] {
  try {
    if (typeof window === "undefined") return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_PRODUCTS).filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function saveToStorage(slugs: string[]): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(slugs));
  } catch {
    // ignore quota errors
  }
}

export function CompareProvider({ children }: { children: ReactNode }): ReactElement {
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>(() => loadFromStorage());

  useEffect(() => {
    saveToStorage(selectedSlugs);
  }, [selectedSlugs]);

  const addProduct = useCallback((slug: string) => {
    setSelectedSlugs((prev) => {
      if (prev.includes(slug)) return prev;
      if (prev.length >= MAX_PRODUCTS) return prev;
      return [...prev, slug];
    });
  }, []);

  const removeProduct = useCallback((slug: string) => {
    setSelectedSlugs((prev) => prev.filter((s) => s !== slug));
  }, []);

  const toggleProduct = useCallback((slug: string): boolean => {
    let added = false;
    setSelectedSlugs((prev) => {
      if (prev.includes(slug)) {
        return prev.filter((s) => s !== slug);
      }
      if (prev.length >= MAX_PRODUCTS) {
        return prev;
      }
      added = true;
      return [...prev, slug];
    });
    return added;
  }, []);

  const isSelected = useCallback((slug: string) => selectedSlugs.includes(slug), [selectedSlugs]);

  const clear = useCallback(() => setSelectedSlugs([]), []);

  const value = useMemo<CompareContextValue>(
    () => ({
      selectedSlugs,
      addProduct,
      removeProduct,
      toggleProduct,
      isSelected,
      clear,
      isFull: selectedSlugs.length >= MAX_PRODUCTS,
      count: selectedSlugs.length,
    }),
    [selectedSlugs, addProduct, removeProduct, toggleProduct, isSelected, clear],
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}

export function useCompare(): CompareContextValue {
  const ctx = useContext(CompareContext);
  if (!ctx) {
    throw new Error("useCompare must be used within <CompareProvider>");
  }
  return ctx;
}

export const COMPARE_MAX = MAX_PRODUCTS;
